import { z } from 'zod';
import { api } from './client';
import {
  cleanParams,
  itemSchema,
  paginatedSchema,
  userSchema,
  type ListParams,
  type Paginated,
  type Role,
  type User,
} from './schemas';

export interface UserListParams extends ListParams {
  q?: string;
  role?: Role | '';
  active?: boolean | '';
}

export interface UserCreateInput {
  username: string;
  name: string;
  password: string;
  role?: Role;
  active?: boolean;
  must_change_password?: boolean;
}

export type UserUpdateInput = Partial<
  Pick<User, 'username' | 'name' | 'role' | 'active' | 'must_change_password'>
>;

export async function listUsers(params: UserListParams = {}): Promise<Paginated<User>> {
  // Laravel's `boolean` rule accepts 1/0 but NOT the strings "true"/"false" that axios would send.
  const { active, ...rest } = params;
  const { data } = await api.get('/users', {
    params: cleanParams({
      ...rest,
      active: active === '' || active === undefined ? undefined : active ? 1 : 0,
    }),
  });
  return paginatedSchema(userSchema).parse(data);
}

export async function getUser(id: number): Promise<User> {
  const { data } = await api.get(`/users/${id}`);
  return itemSchema(userSchema).parse(data).data;
}

export async function createUser(input: UserCreateInput): Promise<User> {
  const { data } = await api.post('/users', input);
  return itemSchema(userSchema).parse(data).data;
}

export async function updateUser(id: number, input: UserUpdateInput): Promise<User> {
  const { data } = await api.patch(`/users/${id}`, input);
  return itemSchema(userSchema).parse(data).data;
}

export async function deleteUser(id: number): Promise<void> {
  await api.delete(`/users/${id}`);
}

const resetSchema = z.looseObject({
  message: z.string().optional(),
  data: userSchema,
  temporary_password: z.string().nullish(),
});

/** Omit `password` to have the server generate a temporary one (returned once as `temporary_password`). */
export async function resetUserPassword(
  id: number,
  password?: string,
): Promise<{ user: User; temporaryPassword: string | null }> {
  const { data } = await api.post(`/users/${id}/reset-password`, password ? { password } : {});
  const parsed = resetSchema.parse(data);
  return { user: parsed.data, temporaryPassword: parsed.temporary_password ?? null };
}
